/** Music service suspension — not a V2_DEVICE_COMMAND. Notify is never sent from the UI. */

export const SUSPEND_CONFIRM_PHRASE = "SUSPEND MUSIC SERVICE";
export const RESTORE_CONFIRM_PHRASE = "RESTORE MUSIC SERVICE";

export type MusicServiceScopeType = "ALL_LOCATIONS" | "LOCATIONS" | "ZONES";

type MusicServiceUiEnv = {
  REACT_APP_STAGE?: string;
  REACT_APP_ENV?: string;
  KK_TEST_MUSIC_SERVICE_SUSPENSION?: string;
};

/** Staging/dev only. Production Super Admin must not render suspend controls. */
export function isMusicServiceSuspensionUiEnabled(
  env: MusicServiceUiEnv = process.env as MusicServiceUiEnv,
): boolean {
  return (
    env.REACT_APP_STAGE === "staging" ||
    env.REACT_APP_ENV === "staging" ||
    env.REACT_APP_ENV === "development" ||
    env.KK_TEST_MUSIC_SERVICE_SUSPENSION === "true"
  );
}

export function buildMusicServicePreviewPath(): string {
  return "v1/sa/device-fleet/music-service/preview";
}
export function buildMusicServiceSuspendPath(): string {
  return "v1/sa/device-fleet/music-service/suspend";
}
export function buildMusicServiceRestorePath(): string {
  return "v1/sa/device-fleet/music-service/restore";
}
export function buildMusicServiceListPath(masterAccountId: string): string {
  return `v1/sa/device-fleet/music-service?masterAccountId=${encodeURIComponent(masterAccountId)}`;
}

export function buildMusicServicePreviewRequest(body: {
  masterAccountId: string;
  scopeType: MusicServiceScopeType;
  locationIds?: string[];
  zoneIds?: string[];
}) {
  return { url: buildMusicServicePreviewPath(), method: "POST" as const, body };
}

export function buildMusicServiceSuspendRequest(body: {
  masterAccountId: string;
  scopeType: MusicServiceScopeType;
  locationIds?: string[];
  zoneIds?: string[];
  gracePeriodAttested: true;
  confirmPhrase: typeof SUSPEND_CONFIRM_PHRASE;
}) {
  return { url: buildMusicServiceSuspendPath(), method: "POST" as const, body };
}

export function buildMusicServiceRestoreRequest(body: {
  masterAccountId: string;
  suspensionId: string;
  confirmPhrase: typeof RESTORE_CONFIRM_PHRASE;
}) {
  return { url: buildMusicServiceRestorePath(), method: "POST" as const, body };
}

export type MusicServiceDeviceRef = {
  master_account_id?: string;
  master_account?: string;
  location_id?: string;
  location_name?: string;
  zone_id?: string;
  zone_name?: string;
};

export type MusicServiceContext = {
  masterAccountId: string;
  masterAccountName: string | null;
  locationId: string | null;
  locationName: string | null;
  zoneId: string | null;
  zoneName: string | null;
};

function nonempty(value: string | null | undefined): string {
  if (typeof value !== "string") return "";
  return value.trim();
}

/** Resolve MA / Location / Zone from the expanded Devices row. No billing-group inference. */
export function resolveMusicServiceContext(
  device: MusicServiceDeviceRef | null | undefined,
): MusicServiceContext | null {
  if (!device) return null;
  const masterAccountId = nonempty(device.master_account_id);
  if (!masterAccountId) return null;
  return {
    masterAccountId,
    masterAccountName: nonempty(device.master_account) || null,
    locationId: nonempty(device.location_id) || null,
    locationName: nonempty(device.location_name) || null,
    zoneId: nonempty(device.zone_id) || null,
    zoneName: nonempty(device.zone_name) || null,
  };
}

/** Default to the selected Zone so All Locations is never the accidental first choice. */
export function defaultScopeTypeFromContext(
  context: MusicServiceContext | null,
): MusicServiceScopeType {
  if (context?.zoneId) return "ZONES";
  if (context?.locationId) return "LOCATIONS";
  return "ALL_LOCATIONS";
}

/** Operator-facing label. Never use a raw UUID as the primary text. */
export function friendlyName(
  name: string | null | undefined,
  fallback = "Not reported",
): string {
  return nonempty(name) || fallback;
}

export function contextPlaceLine(context: MusicServiceContext | null): string {
  if (!context) return "";
  return [friendlyName(context.locationName, ""), friendlyName(context.zoneName, "")]
    .filter(Boolean)
    .join(" · ");
}

/** Account · Location on the first compact header line. Zone is secondary. */
export function contextHeaderPrimary(context: MusicServiceContext | null): string {
  if (!context) return "";
  return [friendlyName(context.masterAccountName, ""), friendlyName(context.locationName, "")]
    .filter(Boolean)
    .join(" · ");
}

export function contextHeaderSecondary(context: MusicServiceContext | null): string {
  if (!context) return "";
  return friendlyName(context.zoneName, "");
}

export function contextHeaderLine(context: MusicServiceContext | null): string {
  if (!context) return "";
  return [contextHeaderPrimary(context), contextHeaderSecondary(context)]
    .filter(Boolean)
    .join(" · ");
}

function namedTarget(
  name: string | null | undefined,
  ids: string[],
  contextId: string | null | undefined,
  singular: string,
  plural: string,
): string {
  const n = nonempty(name);
  if (n && ids.length === 1 && contextId && ids[0] === contextId) return n;
  if (n && ids.length === 0 && contextId) return n;
  if (ids.length === 1) return `1 ${singular}`;
  if (ids.length > 1) return `${ids.length} ${plural}`;
  return "(none selected)";
}

/**
 * Exact affected-scope summary for operators.
 * Uses friendly names. IDs stay on the request body, not in this label.
 */
export function describeAffectedScope(input: {
  context?: MusicServiceContext | null;
  scopeType: MusicServiceScopeType;
  locationIds?: string[];
  zoneIds?: string[];
}): string {
  const context = input.context || null;
  const ma = friendlyName(context?.masterAccountName, "this Master Account");
  if (input.scopeType === "ALL_LOCATIONS") {
    return `All Locations under ${ma}`;
  }
  if (input.scopeType === "LOCATIONS") {
    const ids = (input.locationIds || []).filter(Boolean);
    const named = namedTarget(
      context?.locationName,
      ids,
      context?.locationId,
      "Location",
      "Locations",
    );
    return `Selected Location: ${named}`;
  }
  const ids = (input.zoneIds || []).filter(Boolean);
  const named = namedTarget(
    context?.zoneName,
    ids,
    context?.zoneId,
    "Zone",
    "Zones",
  );
  return `Selected Zone: ${named}`;
}

export const SCOPE_TYPE_LABELS: Record<MusicServiceScopeType, string> = {
  ZONES: "Selected Zone",
  LOCATIONS: "Selected Location",
  ALL_LOCATIONS: "All Locations",
};

export const SCOPE_TYPE_OPTIONS: { value: MusicServiceScopeType; label: string }[] = [
  { value: "ZONES", label: SCOPE_TYPE_LABELS.ZONES },
  { value: "LOCATIONS", label: SCOPE_TYPE_LABELS.LOCATIONS },
  { value: "ALL_LOCATIONS", label: SCOPE_TYPE_LABELS.ALL_LOCATIONS },
];

/**
 * Inline layout warning for broad or incomplete targets only.
 * A single named Zone/Location is already visible in the adjacent selector.
 */
export function shouldShowLayoutScopeWarning(input: {
  scopeType: MusicServiceScopeType;
  locationIds?: string[];
  zoneIds?: string[];
}): boolean {
  if (input.scopeType === "ALL_LOCATIONS") return false;
  if (input.scopeType === "LOCATIONS") {
    return (input.locationIds || []).filter(Boolean).length !== 1;
  }
  return (input.zoneIds || []).filter(Boolean).length !== 1;
}
